import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useRacing } from "../context/RacingContext";
import { useAuth } from "../context/AuthContext";
import { StatCard } from "../components/StatCard";
import { RunCard } from "../components/RunCard";
import CheckerBackground from "../components/CheckerBackground";
import { Button } from "../components/ui/button";
import { Gauge, Zap, Trophy, Timer, PlusCircle, ArrowRight, Flag } from "lucide-react";

function bestNum(runs, key, mode = "min") {
  const nums = runs.map((r) => parseFloat(r[key])).filter((n) => !isNaN(n) && n > 0);
  if (!nums.length) return "—";
  return (mode === "min" ? Math.min(...nums) : Math.max(...nums)).toFixed(mode === "min" ? 3 : 1);
}

export default function Dashboard() {
  const { runs, loaded } = useRacing();
  const { user } = useAuth();
  const navigate = useNavigate();

  const stats = useMemo(() => {
    const wins = runs.filter((r) => r.result === "win").length;
    const losses = runs.filter((r) => r.result === "loss").length;
    const winPct = wins + losses ? Math.round((wins / (wins + losses)) * 100) : 0;
    return {
      total: runs.length,
      bestET: bestNum(runs, "et", "min"),
      bestSpeed: bestNum(runs, "speed", "max"),
      bestRT: bestNum(runs.filter((r) => parseFloat(r.reactionTime) >= 0), "reactionTime", "min"),
      wins, losses, winPct,
    };
  }, [runs]);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-mt-text p-6 text-white sm:p-8">
        <CheckerBackground />
        <div className="relative">
          <div className="flex items-center gap-2 text-mt-accent">
            <Flag size={16} /> <span className="text-xs font-bold uppercase tracking-[0.25em]">Your Garage</span>
          </div>
          <h1 className="mt-2 font-head text-3xl font-black tracking-tighter sm:text-4xl">
            {greeting()}, {user?.name?.split(" ")[0] || "Racer"}
          </h1>
          <p className="mt-1 text-white/70">
            {stats.total ? `${stats.total} runs logged this season` : "Log your first run to start tracking."}
          </p>
          <Button onClick={() => navigate("/add")} data-testid="dashboard-log-run" className="mt-5 gap-2 bg-mt-primary text-white hover:bg-mt-primary/90">
            <PlusCircle size={18} /> Log a Run
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Best ET" value={stats.bestET} unit="s" icon={Gauge} accent testid="stat-best-et" />
        <StatCard label="Top Speed" value={stats.bestSpeed} unit="mph" icon={Zap} testid="stat-top-speed" />
        <StatCard label="Best R/T" value={stats.bestRT} unit="s" icon={Timer} testid="stat-best-rt" />
        <StatCard label="Win Rate" value={`${stats.winPct}%`} icon={Trophy} testid="stat-win-rate" />
      </div>

      {/* Win/loss bar */}
      {(stats.wins + stats.losses) > 0 && (
        <div className="rounded-xl border border-mt-border bg-mt-card p-4 shadow-sm">
          <div className="mb-2 flex items-center justify-between text-sm font-semibold">
            <span>Record</span>
            <span className="font-mono">{stats.wins}W · {stats.losses}L</span>
          </div>
          <div className="flex h-3 overflow-hidden rounded-full bg-mt-secondary">
            <div className="bg-mt-success" style={{ width: `${(stats.wins / (stats.wins + stats.losses)) * 100}%` }} />
            <div className="bg-mt-text" style={{ width: `${(stats.losses / (stats.wins + stats.losses)) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Recent runs */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-head text-xl font-bold">Recent Runs</h2>
          {runs.length > 0 && (
            <button onClick={() => navigate("/history")} data-testid="view-all-runs" className="flex items-center gap-1 text-sm font-semibold text-mt-primary hover:underline">
              View all <ArrowRight size={14} />
            </button>
          )}
        </div>
        {!loaded ? (
          <div className="rounded-xl border border-mt-border bg-mt-card p-8 text-center text-mt-mutedfg">Loading…</div>
        ) : runs.length === 0 ? (
          <EmptyState navigate={navigate} />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {runs.slice(0, 4).map((r, i) => <RunCard key={r.id} run={r} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function EmptyState({ navigate }) {
  return (
    <div className="rounded-xl border border-dashed border-mt-border bg-mt-card p-10 text-center">
      <Flag size={32} className="mx-auto text-mt-mutedfg" />
      <h3 className="mt-3 font-head text-lg font-bold">No runs yet</h3>
      <p className="mt-1 text-sm text-mt-mutedfg">Scan a timeslip or log a run manually to get rolling.</p>
      <Button onClick={() => navigate("/add")} data-testid="empty-log-run" className="mt-4 gap-2 bg-mt-primary text-white hover:bg-mt-primary/90">
        <PlusCircle size={16} /> Log your first run
      </Button>
    </div>
  );
}
